import { useCompleteSignUp } from '@/features/authorization/api/authorize'
import { useFetchUser } from '@/features/authorization/api/useFetchUser'
import { useSessionProvider } from '@/features/authorization/components/SessionProvider'
import { Button, Input } from '@/ui-kit'
import CheckBox from '@/ui-kit/components/Checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/ui-kit/components/Dialog'
import { AxiosError } from 'axios'
import { useCallback, useRef, useState } from 'react'
import { URLS } from './urls'

export const SignUpModal = ({
  refCode,
  open,
  setIsOpen
}: {
  refCode?: string
  open: boolean
  setIsOpen: (value: boolean) => void
}) => {
  const { logout } = useSessionProvider()
  const { refetch: refetchUser } = useFetchUser(false)
  const { mutateAsync: completeAuth } = useCompleteSignUp()

  const inputRef = useRef<HTMLInputElement | null>(null)
  const [isChecked, setIsChecked] = useState(true)
  const [isError, setIsError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [inviteCode, setInviteCode] = useState(refCode || '')
  const isRefCodeValid = true

  const avoidDefaultDomBehavior = (e: Event) => {
    e.preventDefault()
  }

  const completeSignUp = useCallback(async () => {
    setIsError(false)
    setErrorMessage('')
    if (!isChecked) {
      setIsError(true)
      return
    }
    try {
      await completeAuth(inviteCode)
      refetchUser()
      setIsOpen(false)
    } catch (err) {
      console.log((err as AxiosError).message)
      setErrorMessage(((err as AxiosError).response?.data as { error: string })?.error || (err as AxiosError).message)
    }
  }, [inviteCode, isChecked, completeAuth, setIsOpen, refetchUser])

  return (
    <Dialog open={open} onOpenChange={setIsOpen}>
      <DialogTrigger />
      <DialogContent
        showCloseButton={false}
        onPointerDownOutside={avoidDefaultDomBehavior}
        onEscapeKeyDown={avoidDefaultDomBehavior}
        className="!max-w-[520px]"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Complete your sign up</DialogTitle>
        </DialogHeader>
        <div className="mt-4 flex flex-col">
          <Input
            className={`colorWhiteToBlack flex-grow rounded-xl ${!isRefCodeValid && 'borderColorError colorError'}`}
            ref={inputRef}
            placeholder="Referral Code"
            value={inviteCode}
            onChange={(value) => setInviteCode(value)}
            isError={!!errorMessage}
            errorText={
              errorMessage.toLowerCase().includes('alias')
                ? 'Referral code is invalid. Please proceed without it, or try another one'
                : errorMessage
            }
          />
          <CheckBox
            defaultChecked={true}
            checked={isChecked}
            error={isError}
            onChange={(value) => setIsChecked(Boolean(value))}
            label={
              <>
                I agree to the{' '}
                <a href={URLS.TERMS_CONDITIONS} target="_blank" className="underline" rel="noreferrer">
                  Terms & Conditions
                </a>{' '}
                and{' '}
                <a href={URLS.PRIVACY_POLICY} target="_blank" className="underline" rel="noreferrer">
                  Privacy Policy
                </a>
              </>
            }
            className="colorGray70to100 mt-4"
          />
          <Button className="mt-4" onClick={() => completeSignUp()}>
            <span className="colorWhite">Continue</span>
          </Button>
          <Button className="mt-4" variant="bordered-gray" onClick={logout}>
            <span className="colorWhite">Logout</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default SignUpModal
